import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import { get, isBoolean } from "lodash";
import PropTypes from "prop-types";

import { getOptionLabel } from "common/enums";
import Collapse from "./Collapse";

const mapBooleanToYesNo = (value) => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return "-";
};

const ElectricalSystemView = ({
  electricalSystem,
  isExpandable = true,
  t,
  isTracking = false,
}) => {
  const {
    isTransformerForBuildingBlock,
    isStandbyPowerAutomatic,
    isElectricSubstation,
    numberOfTransformers,
    transformerType,
    transformerCapacity,
    transformerLocation,
    isSubstation,
    isStandbyPower,
    standbyPowerLocation,
    isUseOfRCCBELCB,
    isEarthing,
    islighteningProtectionSystem,
    typeOfFireProtectionSystemForTransformer,
  } = electricalSystem;
  const fields = useMemo(
    () => [
      {
        name: "Electric Substation",
        key: "isElectricSubstation",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isElectricSubstation) : true,
      },
      {
        name: "Transformer for this building block",
        key: "isTransformerForBuildingBlock",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isTransformerForBuildingBlock) : true,
      },
      {
        name: "Number of Transformers",
        key: "numberOfTransformers",
        isVisible: isTracking
          ? !!numberOfTransformers
          : isTransformerForBuildingBlock,
      },
      {
        name: "Type of transformers",
        key: "transformerType",
        renderField: (value) =>
          getOptionLabel("transformerTypeEnum", value) ?? "-",
        isVisible: isTracking
          ? !!transformerType
          : isTransformerForBuildingBlock,
      },
      {
        name: "Transformer Capacity (KVA)",
        key: "transformerCapacity",
        isRequired: true,
        isVisible: isTracking
          ? !!transformerCapacity
          : isTransformerForBuildingBlock,
      },
      {
        name: "Location of Transformer",
        key: "transformerLocation",
        renderField: (value) =>
          getOptionLabel("transformerLocationEnum", value) ?? "-",
        isVisible: isTracking
          ? !!transformerLocation
          : isTransformerForBuildingBlock,
      },
      {
        name: "Type of fire protection systems provided for transformers",
        key: "typeOfFireProtectionSystemForTransformer",
        isRequired: false,
        isVisible: isTracking
          ? !!typeOfFireProtectionSystemForTransformer
          : true,
      },
      {
        name: "Is there any substation also in this building block?",
        key: "isSubstation",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isSubstation) : true,
      },
      {
        name: "Is there any type of stand by power supply or generator etc provided for the building block?",
        key: "isStandbyPower",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isStandbyPower) : true,
      },
      {
        name: "Is generator or power supply automatic in action?",
        key: "isStandbyPowerAutomatic",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isStandbyPowerAutomatic) : true,
      },
      {
        name: "Power or Generator Location",
        key: "standbyPowerLocation",
        renderField: (value) =>
          getOptionLabel("transformerLocationEnum", value) ?? "-",
        isVisible: isTracking
          ? !!standbyPowerLocation
          : isStandbyPowerAutomatic,
      },
      {
        name: "Are use of correct milli amps RCCB/ELCB?",
        key: "isUseOfRCCBELCB",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isUseOfRCCBELCB) : true,
      },
      {
        name: "Whether body earthing is provided in all the panels and hinges?",
        key: "isEarthing",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isEarthing) : true,
      },
      {
        name: "Has there been a lightening protection system provided as per latest NBC guideline",
        key: "islighteningProtectionSystem",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(islighteningProtectionSystem) : true,
      },
    ],
    [
      isStandbyPowerAutomatic,
      isTransformerForBuildingBlock,
      isElectricSubstation,
      numberOfTransformers,
      transformerType,
      transformerCapacity,
      transformerLocation,
      isSubstation,
      isStandbyPower,
      standbyPowerLocation,
      isUseOfRCCBELCB,
      isEarthing,
      islighteningProtectionSystem,
      typeOfFireProtectionSystemForTransformer,
    ]
  );

  const isCollapseVisible = useMemo(
    () => fields.some(({ isVisible }) => isVisible),
    [fields]
  );

  return (
    <>
      {isCollapseVisible && (
        <Collapse title="Electrical System" isExpandable={isExpandable}>
          <Row className="d-flex mb-2">
            {fields.map(({ name, key, isVisible, isRequired, renderField }) => {
              const value = get(electricalSystem, key, "");
              if (typeof isVisible !== "boolean" || isVisible) {
                return (
                  <Col key={key} lg={6} className="mb-1">
                    <Row>
                      <Col md={5}>{t(name)}:</Col>
                      <Col
                        md={7}
                        className="fw-semibold"
                        style={{ wordBreak: "break-word" }}
                      >
                        {renderField
                          ? renderField(value)
                          : get(electricalSystem, key, null) ??
                            (isRequired ? "-" : "N/A")}
                      </Col>
                    </Row>
                  </Col>
                );
              }
            })}
          </Row>
        </Collapse>
      )}
    </>
  );
};

ElectricalSystemView.propTypes = {
  electricalSystem: PropTypes.object,
  isExpandable: PropTypes.bool,
  t: PropTypes.func,
  isTracking: PropTypes.bool,
};

export default withTranslation()(ElectricalSystemView);
